/**
 * 监控 Webhook 推送记录列表
 * 路由：/client/ys7/log
 */
import React from 'react'
import { connect } from 'react-redux'
import { Button, Card, message, Table, Tag, Tooltip, Typography } from 'antd'
import dayjs from 'dayjs'
import PageHeader from '@/components/PageHeader'
import SimpleModal from '@/components/SimpleModal/Modal'
import HeaderControl from '@/components/HeaderControl'
import { InputC, RangePickerC, SelectC } from '@/i2antdV5'
import { fetchMonitoringLogs } from '@/redux/alerts/monitoringLogsSlice'
import { formatDateTimeSecondsStr, renderTextStr } from '@/utils/renderUtils'
import MarkMonitoringLogFalsePositiveModal from './share/MarkMonitoringLogFalsePositiveModal'
import './Log.less'

const { Text } = Typography

const breadcrumbList = [
  { breadcrumbName: '首页', href: '/client/commandCenter' },
  { breadcrumbName: '预警记录' }
]

const MESSAGE_TYPE_OPTIONS = [
  { id: 'ys.open.ai.agent', value: '萤石云' },
  { id: 'awl.open.ai.agent', value: '奥威龙' }
]

const MESSAGE_TYPE_LABEL_MAP = MESSAGE_TYPE_OPTIONS.reduce((acc, item) => {
  acc[item.id] = item.value
  return acc
}, {})

/** 布尔 query 筛选：null/不传 = 全部；true = 是；false = 否 */
const BOOL_FILTER_OPTIONS = [
  { id: 'true', value: '是' },
  { id: 'false', value: '否' }
]

const BOOL_FILTER_CONFIG = [
  { searchKey: 'hasBaby', label: '有宝宝' },
  { searchKey: 'isFaceOccluded', label: '遮挡' },
  { searchKey: 'isViolation', label: '异常' },
  { searchKey: 'isFalsePositive', label: '误报' }
]

const BOOL_FILTER_KEYS = BOOL_FILTER_CONFIG.map(({ searchKey }) => searchKey)

const normalizeMonitoringLogsBoolQuery = (query = {}) => {
  const next = { ...query }
  BOOL_FILTER_KEYS.forEach((key) => {
    const raw = next[key]
    if (raw == null || raw === '') {
      delete next[key]
      return
    }
    if (raw === true || raw === 'true') {
      next[key] = true
      return
    }
    if (raw === false || raw === 'false') {
      next[key] = false
      return
    }
    delete next[key]
  })
  return next
}

const MONITORING_LOGS_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm:ss'

/** 列表默认查询（页面侧定义，Redux 只落库） */
const DEFAULT_MONITORING_LOGS_QUERY = {
  pageNumber: 1,
  pageSize: 20,
  orderBy: 'create_time',
  orderDirection: 'desc',
  startTime: dayjs().subtract(7, 'day').startOf('day').format(MONITORING_LOGS_DATETIME_FORMAT),
  endTime: dayjs().endOf('day').format(MONITORING_LOGS_DATETIME_FORMAT)
}

const mergeMonitoringLogsQuery = (reduxQuery) =>
  reduxQuery && Object.keys(reduxQuery).length > 0 ? reduxQuery : DEFAULT_MONITORING_LOGS_QUERY

function monitoringLogRowKey (record) {
  if (record?.id != null && record.id !== '') return String(record.id)
  if (record?.messageId) return `msg-${record.messageId}`
  return `log-${record?.deviceId ?? ''}-${record?.channelNo ?? ''}-${record?.messageTime ?? ''}`
}

function renderBoolTag (value) {
  if (value === true) return <Tag color="green">是</Tag>
  if (value === false) return <Tag color="default">否</Tag>
  return renderTextStr(null)
}

function firstUrlInArray (arr) {
  if (!Array.isArray(arr) || arr.length === 0) return ''
  const first = arr.find((u) => typeof u === 'string' && u.trim() !== '')
  return first ? first.trim() : ''
}

function getFirstThumbnailUrl (record) {
  return firstUrlInArray(record?.thumbnailUrls) || firstUrlInArray(record?.imageUrls)
}

function getFirstFullImageUrl (record) {
  return firstUrlInArray(record?.imageUrls) || firstUrlInArray(record?.thumbnailUrls)
}

function isMonitoringLogFalsePositive (record) {
  return record?.isFalsePositive === true || record?.falsePositive === true
}

class MonitoringLogsPage extends React.Component {
  componentDidMount () {
    this.fetchLogs(DEFAULT_MONITORING_LOGS_QUERY)
  }

  fetchLogs = (formatQuery) => {
    const query = normalizeMonitoringLogsBoolQuery(formatQuery)
    this.props
      .fetchMonitoringLogs({ query })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载预警记录失败'))
  }

  handleHeaderSearch = (formatQuery) => {
    this.fetchLogs({
      ...formatQuery,
      pageNumber: 1
    })
  }

  handleTableChange = (pagination) => {
    const query = mergeMonitoringLogsQuery(this.props.monitoringLogs.query)
    this.fetchLogs({
      ...query,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    })
  }

  handleMarkFalsePositiveSuccess = () => {
    const query = mergeMonitoringLogsQuery(this.props.monitoringLogs.query)
    this.fetchLogs(query)
  }

  getColumns = () => [
    {
      title: '消息类型',
      dataIndex: 'messageType',
      width: 120,
      ellipsis: true,
      render: (text) => {
        const raw = text != null && text !== '' ? String(text).trim() : ''
        if (!raw) return renderTextStr(null)
        const label = MESSAGE_TYPE_LABEL_MAP[raw] || raw
        return <Tag>{label}</Tag>
      }
    },
    {
      title: '推送时间',
      dataIndex: 'messageTime',
      width: 150,
      render: (t) => formatDateTimeSecondsStr(t)
    },
    {
      title: '设备',
      dataIndex: 'deviceId',
      width: 120,
      ellipsis: true,
      render: (v) => renderTextStr(v)
    },
    {
      title: '通道',
      dataIndex: 'channelNo',
      width: 64,
      render: (v) => renderTextStr(v)
    },
    {
      title: '摘要',
      dataIndex: 'summary',
      width: 200,
      ellipsis: true,
      render: (text) => {
        const displayText = renderTextStr(text)
        return (
          <Tooltip title={displayText} placement="topLeft">
            <span>{displayText}</span>
          </Tooltip>
        )
      }
    },
    {
      title: '图片',
      key: 'thumbnailUrls',
      width: 72,
      render: (_, record) => {
        const preview = getFirstThumbnailUrl(record)
        if (!preview) return renderTextStr(null)
        const full = getFirstFullImageUrl(record)
        return (
          <SimpleModal baseProps={{ url: full || preview }}>
            <img src={preview} alt="" className="ys-monitoring-logs__thumb" />
          </SimpleModal>
        )
      }
    },
    {
      title: '有宝宝',
      dataIndex: 'hasBaby',
      width: 72,
      render: renderBoolTag
    },
    {
      title: '遮挡',
      dataIndex: 'isFaceOccluded',
      width: 64,
      render: renderBoolTag
    },
    {
      title: '异常',
      dataIndex: 'isViolation',
      width: 64,
      render: (v) =>
        v === true ? <Tag color="red">是</Tag> : v === false ? <Tag color="default">否</Tag> : renderTextStr(null)
    },
    {
      title: '消息ID',
      dataIndex: 'messageId',
      width: 200,
      render: (v) => {
        return (
          <Text code copyable={{ text: renderTextStr(v) }} ellipsis={{ tooltip: renderTextStr(v) }}>
            {renderTextStr(v)}
          </Text>
        )
      }
    },
    {
      title: '图片名字',
      dataIndex: 'filename',
      width: 260,
      render: (v) => {
        return (
          <Text code copyable={{ text: renderTextStr(v) }} ellipsis={{ tooltip: renderTextStr(v) }}>
            {renderTextStr(v)}
          </Text>
        )
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 150,
      render: (t) => renderTextStr(t)
    },
    {
      title: '操作',
      key: 'action',
      width: 110,
      fixed: 'right',
      render: (_, record) => {
        const id = record?.id
        if (id == null || id === '') return renderTextStr(null)
        if (isMonitoringLogFalsePositive(record)) {
          return <Tag>已标误报</Tag>
        }
        return (
          <MarkMonitoringLogFalsePositiveModal
            baseProps={{ record }}
            onSuccess={this.handleMarkFalsePositiveSuccess}
          >
            <Button type="link" size="small" danger>
              标成误报
            </Button>
          </MarkMonitoringLogFalsePositiveModal>
        )
      }
    }
  ]

  render () {
    const { monitoringLogs } = this.props
    const { fetching, query: reduxQuery, data = {} } = monitoringLogs
    const query = mergeMonitoringLogsQuery(reduxQuery)
    const { list = [], totalRow = 0, pageNumber = 1, pageSize = 20 } = data

    return (
      <div className="ys-monitoring-logs">
        <PageHeader breadcrumbList={breadcrumbList} />
        <div className="i2-container">
          <Card className="i2-card-page">
            <section className="i2-app-toolbar">
              <HeaderControl
                colSpan="b2"
                query={query}
                fetching={fetching}
                action={this.handleHeaderSearch}
                searchText="查询"
              >
                <InputC
                  label="消息ID"
                  searchKey="messageId"
                  modalProps={{ allowClear: true, placeholder: '消息ID' }}
                />
                <InputC
                  label="设备序列号"
                  searchKey="deviceId"
                  modalProps={{ allowClear: true, placeholder: '设备序列号' }}
                />
                <InputC
                  label="通道号"
                  searchKey="channelNo"
                  modalProps={{ allowClear: true, placeholder: '通道号' }}
                />
                <SelectC
                  label="消息类型"
                  searchKey="messageType"
                  options={MESSAGE_TYPE_OPTIONS}
                  modalProps={{ allowClear: true, placeholder: '全部' }}
                />
                {BOOL_FILTER_CONFIG.map(({ searchKey, label }) => (
                  <SelectC
                    key={searchKey}
                    label={label}
                    searchKey={searchKey}
                    options={BOOL_FILTER_OPTIONS}
                    style={{ minWidth: 120 }}
                    modalProps={{ allowClear: true, placeholder: '全部' }}
                  />
                ))}
                <RangePickerC
                  label="时间范围"
                  searchKey={['startTime', 'endTime']}
                  dateStrValue={true}
                  modalProps={{
                    showTime: { format: 'HH:mm:ss' },
                    format: MONITORING_LOGS_DATETIME_FORMAT,
                    allowClear: false,
                    style: { width: 360 }
                  }}
                />
              </HeaderControl>
            </section>
            <Table
              loading={fetching}
              columns={this.getColumns()}
              dataSource={list}
              rowKey={monitoringLogRowKey}
              size="small"
              tableLayout="fixed"
              scroll={{ x: 1510 }}
              onChange={this.handleTableChange}
              pagination={{
                current: pageNumber,
                pageSize,
                total: totalRow,
                showSizeChanger: true,
                showTotal: (t) => `共 ${t} 条`,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
            />
          </Card>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  monitoringLogs: state.alerts.monitoringLogs
})

const mapDispatchToProps = {
  fetchMonitoringLogs
}

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringLogsPage)
